import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Clock,
  Layers,
  FileCode,
  GitBranch,
  Database,
  Settings,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import ForwardPathsDiagram from '@/components/diagrams/ForwardPathsDiagram';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_DATES = [
  {
    date: 'December 2026',
    label: 'WebCenter 12c Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c — including WebCenter Content, Imaging, Forms Recognition, Enterprise Capture, Portal, and Sites — exits Premier Support. No further new features, OS certifications, or non-critical updates after this date.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'WebCenter 12c Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy. Unusually short at 12 months. Extended Support provides security updates and critical patches but not general bug fixes.',
    severity: 'red',
  },
  {
    date: '2030 / 2033',
    label: 'WebCenter 14c support window',
    detail:
      'Oracle Fusion Middleware 14c (14.1.2) is supported through December 2030 (Premier) and December 2033 (Extended). Upgrading from 12c to 14c buys approximately 8 years of supported runway.',
    severity: 'green',
  },
];

const WHATS_NEW = [
  {
    icon: Sparkles,
    title: 'Cloud-native architecture',
    detail:
      'WebCenter 14c is built for OCI deployment with containerized clusters running on OCI Kubernetes Engine, elastic scaling, and integration with OCI Object Storage for content repositories.',
  },
  {
    icon: Database,
    title: 'REST APIs across the stack',
    detail:
      'Native REST API surface across WebCenter Content, Imaging, and Capture. Replaces SOAP-only and proprietary integration patterns from earlier releases.',
  },
  {
    icon: Layers,
    title: 'AI integration points',
    detail:
      'Built-in hooks for Oracle\'s AI services — Document Understanding, Generative AI Service — embedded into content workflows. AI Overview describes the platform as integrating "AI and machine learning to automatically capture, validate, and post vendor invoices."',
  },
  {
    icon: Settings,
    title: 'OCI Object Storage migration',
    detail:
      'WebCenter Content 14c supports migration of file stores to OCI Object Storage via the Object Storage Migration Tool — built directly on top of the File Store Provider foundation.',
  },
];

const UPGRADE_PATH = [
  {
    step: '1',
    title: 'Inventory current 12c implementation',
    body: 'Document every customization, workflow hook, integration, Forms Recognition project, SOA composite, WebLogic configuration, ADF customization, and FIPSA accelerator. Surface dependencies on deprecated 12c features (e.g., Jive features in WebCenter Portal, Telestream FlipFactory) that need different handling in 14c.',
  },
  {
    step: '2',
    title: 'Out-of-place domain directory upgrade',
    body: 'Oracle\'s recommended 12c → 14c upgrade pattern uses Oracle\'s Upgrade Assistant for an out-of-place domain directory upgrade — a new 14c domain is created alongside the 12c domain, then the 12c configuration is migrated forward. In-place upgrades are not recommended for 12c → 14c.',
  },
  {
    step: '3',
    title: 'Migrate components, customizations, and integrations',
    body: 'Custom WCC components, Forms Recognition projects, SOA composites, WebLogic deployments, and FIPSA configurations are re-deployed in the new 14c domain. Custom Java services and iDoc Script require validation against 14c API changes. ADF customizations need ADF 14c compatibility review.',
  },
  {
    step: '4',
    title: 'Database and integration upgrade',
    body: 'Database tier upgraded to a 14c-compatible version (typically 19c or higher). EBS / Fusion / IDCS integrations re-tested. OIC integrations validated. AME workflows reviewed for 14c routing changes.',
  },
  {
    step: '5',
    title: 'Migrate file stores to OCI Object Storage (optional but recommended)',
    body: 'For large content repositories (500,000+ files), the WebCenter Content Object Storage Migration Tool exports source files and metadata into OCI Object Storage — the recommended target for 14c-on-OCI deployments.',
  },
  {
    step: '6',
    title: 'Parallel run, UAT, cutover',
    body: 'Run 12c and 14c in parallel during UAT. Validate every workflow path, Forms Recognition extraction, SOA composite, and ERP integration. Cut over only after end-to-end retrievability and posting testing.',
  },
];

const PATH_COMPARISON = [
  {
    dimension: 'What changes',
    pathA: 'WebCenter architecture preserved; infrastructure modernized (14c features + OCI)',
    pathB: 'AP automation layer replaced with modern AI-driven platform; Oracle ERP stays',
  },
  {
    dimension: 'Customer effort',
    pathA: '6-12 months upgrade project; team learns 14c-specific changes',
    pathB: '4-9 months modernization project; team learns modern AP automation platform',
  },
  {
    dimension: 'WebCenter customizations',
    pathA: 'Carried forward into 14c (with validation work)',
    pathB: 'Translated forward into the modern platform; some simplify away',
  },
  {
    dimension: 'AI capabilities',
    pathA: 'WebCenter 14c\'s embedded AI hooks (Document Understanding, etc.)',
    pathB: 'AI-native AP automation with continuous-learning extraction',
  },
  {
    dimension: 'Next decision point',
    pathA: 'WebCenter 14c Premier Support ends Dec 2030 — same decision again',
    pathB: 'Strategic decision made once',
  },
  {
    dimension: 'Total cost of ownership',
    pathA: 'Upgrade + ongoing WebCenter license + ongoing infra + 2030 decision cost',
    pathB: 'Implementation + ongoing SaaS / FDE engagement; no WebCenter license',
  },
];

const FAQS = [
  {
    q: 'What is Oracle WebCenter 14c?',
    a: 'Oracle WebCenter 14c (release 14.1.2) is the latest major version of the Oracle WebCenter product family, including WebCenter Content (UCM), WebCenter Imaging, WebCenter Enterprise Capture, WebCenter Forms Recognition, WebCenter Portal, and WebCenter Sites. 14c is cloud-native (OCI-ready), includes REST API surfaces, integrates with Oracle AI services (Document Understanding, Generative AI), and supports OCI Object Storage as a content repository tier. Per Oracle\'s Lifetime Support Policy, 14c carries approximately 8 years of supported runway from release.',
  },
  {
    q: 'When does WebCenter 12c support end?',
    a: 'Oracle Fusion Middleware 12c — covering all WebCenter products — Premier Support ends December 2026. Extended Support ends December 2027 (unusually short at 12 months). After December 2027, the only Oracle-provided option is Sustaining Support (limited patching) or Market Driven Support (yearly-renewable, costs more than Extended Support). Customers on 12c face a decision window in 2026-2027.',
  },
  {
    q: 'How does the 12c to 14c upgrade work?',
    a: 'Oracle\'s recommended upgrade pattern is an out-of-place domain directory upgrade using Oracle\'s Upgrade Assistant. A new 14c domain is created alongside the existing 12c domain; the 12c configuration is migrated forward into the new domain; custom components, Forms Recognition projects, SOA composites, WebLogic deployments, and ADF customizations are re-deployed and validated against 14c. In-place upgrades are not recommended for 12c → 14c.',
  },
  {
    q: 'What customizations transfer forward to 14c?',
    a: 'Most WCC custom components, iDoc Script customizations, custom services, and security models transfer forward — with validation work for any API changes. SOA composites generally migrate via Oracle\'s Upgrade Assistant. Forms Recognition projects (templates, definitions, learned data) transfer with template-format validation. ADF customizations need ADF 14c compatibility review. Custom Java services compiled against 12c APIs require recompilation and validation.',
  },
  {
    q: 'What deprecated features in 12c are affected by the 14c upgrade?',
    a: 'Certain 12c features were deprecated or removed in earlier updates and are not available in 14c. Examples include the Jive features in WebCenter Portal and certain integrations like Telestream FlipFactory in WebCenter Content. The Inventory & Assessment phase of an upgrade should explicitly surface dependencies on deprecated features so they can be replaced before cutover.',
  },
  {
    q: 'Should we upgrade to 14c or modernize to a different platform?',
    a: 'Both are legitimate forward paths. The 14c upgrade preserves the WebCenter architecture and buys approximately 8 years of supported runway (Premier Support through 2030, Extended through 2033). Modernization to a different AP automation platform (Path B in our framework) replaces the AP layer entirely while preserving Oracle ERP as the system of record. The right answer depends on stack complexity, AP team requirements, and whether you want to make this decision once or face the same cycle again in 2030-2032. See our [WebCenter Decision Guide] for the full framework.',
  },
  {
    q: 'Can EZ Cloud help with a 12c to 14c upgrade?',
    a: 'Yes. The same 25-year founder-level WebCenter expertise that powers EZ Cloud\'s modernization assessments applies equally to 12c → 14c upgrade engagements. EZ Cloud offers fixed-fee architecture review, FIPSA-specific assessments, Forms Recognition rules migration audits, OCI architecture and security setup, and full 12c → 14c upgrade planning. Engagement pricing is established per situation on a Decision Call.',
  },
  {
    q: 'How long does a 12c to 14c upgrade typically take?',
    a: 'Typical timelines range 6-12 months depending on stack complexity. Implementations with extensive Forms Recognition template libraries, sophisticated SOA composite workflows, FIPSA accelerator customizations, or heavy ADF non-PO coding form customizations land at the longer end. Implementations closer to Oracle\'s reference architecture land at the shorter end. The Inventory & Assessment phase typically runs 2-4 weeks and surfaces the right timeline for your specific implementation.',
  },
];

const WebCenter14cUpgrade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to upgrade Oracle WebCenter from 12c to 14c',
    description: 'The Oracle-recommended out-of-place domain directory upgrade pattern for Oracle WebCenter 12c to 14c (14.1.2). Covers inventory, out-of-place upgrade, customization migration, database/integration upgrade, file store migration, and parallel run.',
    totalTime: 'P12M',
    step: UPGRADE_PATH.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/webcenter-14c-upgrade#step-' + (idx + 1),
    })),
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle WebCenter 12c to 14c Upgrade — The December 2026 Decision Path',
    description:
      'Oracle WebCenter 12c Premier Support ends December 2026. The 12c to 14c upgrade is one of two primary forward paths. Out-of-place domain directory upgrade pattern, customization migration, OCI Object Storage migration, and engagement options for the upgrade window.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-14c-upgrade',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter 14c' },
      { '@type': 'Thing', name: 'Oracle WebCenter Content 14.1.2' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 14c' },
      { '@type': 'Thing', name: 'Oracle WebCenter 12c' },
      { '@type': 'Thing', name: 'Oracle Upgrade Assistant' },
      { '@type': 'Thing', name: 'OCI Object Storage' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter 12c to 14c Upgrade — The December 2026 Decision Path | EZ Cloud"
        description="Oracle WebCenter 12c Premier Support ends December 2026. The 12c to 14c (14.1.2) upgrade — out-of-place domain directory upgrade pattern, what's new in 14c, customization migration, OCI Object Storage migration, and engagement options for the upgrade window."
        canonical="https://ezcloud.ai/solutions/webcenter-14c-upgrade"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter 14c Upgrade',
            url: 'https://ezcloud.ai/solutions/webcenter-14c-upgrade',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Clock className="w-4 h-4" aria-hidden />
              The Path A+ Upgrade · Oracle WebCenter 14c
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter 12c to 14c Upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Out-of-place upgrade. 8 years of runway. The December 2026 decision path.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle WebCenter 12c Premier Support ends December 2026. The upgrade to WebCenter 14c
              (release 14.1.2) is one of two primary forward paths — preserves the WebCenter
              architecture, modernizes the infrastructure, and buys approximately 8 years of
              supported runway.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-14c-upgrade-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a 14c Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — definitional summary for AEO synthesis */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle WebCenter 12c to 14c upgrade, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter 14c (release 14.1.2) is the latest major version of the Oracle
                  WebCenter product family — WebCenter Content (UCM), WebCenter Imaging, WebCenter
                  Enterprise Capture, WebCenter Forms Recognition, WebCenter Portal, and WebCenter
                  Sites.
                </strong>{' '}
                14c is cloud-native (OCI-ready), exposes REST APIs across the stack, includes
                integration points for Oracle AI services (Document Understanding, Generative AI),
                and supports OCI Object Storage as a content repository tier.
              </p>
              <p>
                The recommended 12c → 14c upgrade pattern is an{' '}
                <strong className="text-gray-900">
                  out-of-place domain directory upgrade using Oracle's Upgrade Assistant
                </strong>
                . A new 14c domain is created alongside the existing 12c domain; the 12c
                configuration is migrated forward; custom WebCenter Content components, Forms
                Recognition projects, SOA composites, WebLogic deployments, ADF customizations, and
                FIPSA accelerators are re-deployed and validated against 14c. In-place upgrades are
                not recommended.
              </p>
              <p>
                Per Oracle's Lifetime Support Policy, WebCenter 14c carries approximately 8 years
                of supported runway — Premier Support through December 2030, Extended Support
                through December 2033. The upgrade preserves the existing WebCenter investment and
                extends the runway, but the same lifecycle decision recurs at the next 14c Premier
                Support window.
              </p>
            </div>
          </div>
        </section>

        {/* Key dates */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Key dates for the 12c → 14c decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. The 12c support cliff and the 14c support
              window together define the decision frame for every WebCenter customer in 2026. For
              every component's dates in one table, see the{' '}
              <Link
                to="/resources/oracle-fusion-middleware-support-dates"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                consolidated FMW support-dates reference
              </Link>
              ; for the whole-stack picture, the{' '}
              <Link
                to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                className="font-semibold text-primary hover:text-accent transition-colors"
              >
                Fusion Middleware 12c end-of-support guide
              </Link>
              .
            </p>
            <div className="space-y-5">
              {KEY_DATES.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's new in 14c — two-column editorial layout (deliberate rhythm
            break from the centered card-grid grammar used elsewhere) */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-28">
                  <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
                    What's new in 14c
                  </p>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                    Four meaningful shifts in WebCenter 14c (14.1.2)
                  </h2>
                  <p className="text-gray-600 leading-relaxed">
                    14c is not just a number bump. The architecture, API surface, AI integration,
                    and storage tier all advance materially over 12c — and each shift changes what
                    an upgraded implementation can do that the 12c one could not.
                  </p>
                </div>
              </div>
              <div className="lg:col-span-3 space-y-4">
                {WHATS_NEW.map(({ icon: Icon, title, detail }) => (
                  <div key={title} className="flex items-start gap-5 border-b border-gray-200 pb-4 last:border-b-0">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* The upgrade path */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The upgrade approach
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The six steps of an Oracle-recommended 12c → 14c upgrade
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Out-of-place domain directory upgrade is the Oracle-recommended pattern. Each step
              has its own scope, risks, and validation requirements.
            </p>
            <div className="space-y-5">
              {UPGRADE_PATH.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Deprecation watch */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-amber-50 border border-amber-200 shadow-sm">
              <div className="flex items-start gap-4">
                <AlertTriangle
                  className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1"
                  aria-hidden
                />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                    Deprecation watch
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Some 12c features are not in 14c
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Certain features deprecated in earlier 12c updates were removed entirely in
                    14c. The Inventory & Assessment phase needs to explicitly surface dependencies
                    on these features so they can be handled before cutover. Common examples:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Jive features in WebCenter Portal — deprecated; alternative collaboration
                      patterns required
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Telestream FlipFactory integration in WebCenter Content — removed; alternative
                      media processing required
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Certain SOA composite patterns from 11g — review for 12c-and-14c compatibility
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* The decision, drawn */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Where the 14c upgrade sits
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              The 14c upgrade is Path A+ of three forward paths
            </h2>
            <ForwardPathsDiagram />
          </div>
        </section>

        {/* Path A+ vs Path B comparison */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Path A+ (upgrade) vs Path B (modernize)
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              How the 14c upgrade compares to modernizing off WebCenter
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Both are legitimate forward paths from the December 2026 cliff. Path A+ extends the
              WebCenter investment; Path B replaces the AP automation layer entirely.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Path A+ — Upgrade to 14c
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Path B — Modernize off WebCenter
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {PATH_COMPARISON.map((row) => (
                    <tr key={row.dimension} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.dimension}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.pathA}</td>
                      <td className="px-5 py-4 text-gray-700">{row.pathB}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-oci-marketplace-vs-modernize"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full Path A / A+ / B / B+ comparison
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on 12c → 14c upgrades
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the 14c upgrade
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The same 25-year founder-level WebCenter expertise that powers EZ Cloud's
              modernization assessments applies equally to 12c → 14c upgrade engagements.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 1 · Upgrade Assessment
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Fixed-fee inventory and forward plan
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Andrew reads your existing 12c WebCenter implementation directly and delivers a
                  12c → 14c upgrade plan. Surfaces every customization, deprecated-feature
                  dependency, integration, and migration consideration. Plan is yours to keep
                  regardless of which delivery partner you choose.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 2 · Architecture Review
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Deeper read of your existing WebCenter implementation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  A one-week fixed-fee read of your running WebCenter — Forms Recognition rules,
                  SOA composites, WebLogic configuration, FIPSA accelerators, ADF customizations,
                  EBS / Fusion integration. Output: structured inventory used as input to any 14c
                  upgrade or modernization decision.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 3 · Strategic Advisory Retainer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Founder-level guidance through your upgrade window
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Monthly retainer with Andrew, sized for the customer working through a 6-12 month
                  upgrade decision with internal stakeholders, procurement, and Oracle. Strategic
                  guidance on architecture choices, customization tradeoffs, and 14c-specific
                  considerations.
                </p>
              </Card>

              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Tier 4 · Specific upgrade consulting outcomes
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Project-based 14c upgrade work
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Forms Recognition rules migration audits, FIPSA-specific upgrade assessments,
                  OCI architecture and security setup, WebLogic 14c migration, ADF 14c
                  compatibility review, file store to OCI Object Storage migration. Project-based,
                  founder-led, scoped to a defined outcome.
                </p>
              </Card>
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to hub + decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, including the 14c upgrade, with
              stack-specific considerations, eight diagnostic questions, and vendor evaluation
              checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the 12c → 14c upgrade
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your 12c → 14c upgrade
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through your stack and the upgrade considerations
              in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-14c-upgrade-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a 14c Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenter14cUpgrade;
